import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { ShieldAlert, FileText, Truck, Lock, FlaskConical } from "lucide-react";
import { Reveal } from "../components/Reveal";

const SECTIONS = [
  {
    id: "research",
    icon: FlaskConical,
    title: "Research Use Policy",
    body: [
      "All products supplied by Recovery Supps Australia are sold strictly for laboratory and in-vitro research use only. They are not for human or animal consumption.",
      "Our products are research chemicals — they are NOT dietary supplements, foods, cosmetics, or therapeutic goods, and are not approved for any therapeutic use. No physiological, medical or health claims are made about any product on this website. Any reference to research is a summary of published scientific literature and is not a claim of benefit.",
      "By purchasing, you certify that you are a qualified researcher or purchasing on behalf of a research organisation, that you understand safe handling of research materials, and that you will not resell or supply products for human consumption.",
      "A Certificate of Analysis (COA) is available for every SKU on its product page.",
    ],
  },
  {
    id: "age",
    icon: ShieldAlert,
    title: "Age Restriction (18+)",
    body: [
      "You must be at least 18 years of age to browse or purchase from this website. By entering the site and completing a purchase you confirm you are 18 or older.",
      "We reserve the right to request proof of age or research credentials and to cancel and refund any order that does not meet our compliance requirements.",
    ],
  },
  {
    id: "shipping",
    icon: Truck,
    title: "Shipping & Returns",
    body: [
      "Orders are dispatched from within Australia via express, tracked, cold-packed shipping where required. Products are shipped to Australian delivery addresses only, in jurisdictions where the products are legal for research supply.",
      "Because these are research materials, we cannot accept returns of opened or temperature-sensitive items. If a product arrives damaged or incorrect, contact us within 7 days of delivery for a replacement or refund.",
      "Title and risk pass to the buyer on delivery. It is the buyer's responsibility to ensure lawful receipt and use in their jurisdiction.",
    ],
  },
  {
    id: "terms",
    icon: FileText,
    title: "Terms & Conditions",
    body: [
      "By placing an order you enter into a contract governed by the laws of Australia. Prices are in AUD and include or exclude tax as shown at checkout.",
      "We may refuse or cancel any order at our discretion, including where we suspect intended human use, resale, or non-compliance with these terms.",
      "To the maximum extent permitted by law, Recovery Supps Australia accepts no liability for any use of products contrary to the Research Use Policy above.",
    ],
  },
  {
    id: "privacy",
    icon: Lock,
    title: "Privacy",
    body: [
      "We collect only the information needed to process and deliver your order and to operate our rewards program. Payment details are handled securely by our payment processor and are never stored on our servers.",
      "We do not sell your personal information. You may request access to or deletion of your data by contacting us.",
    ],
  },
];

export default function Policies() {
  const { hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 200);
    } else {
      window.scrollTo(0, 0);
    }
  }, [hash]);

  return (
    <div className="pt-24 md:pt-28 pb-24">
      <div className="border-b border-[#27272A] bg-ink-800/30">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-20">
          <span className="font-mono-tech text-xs tracking-[0.3em] uppercase text-accent">
            / Compliance & Legal
          </span>
          <h1 className="font-display font-black tracking-tighter text-5xl sm:text-6xl lg:text-7xl mt-4 leading-none">
            POLICIES
          </h1>
          <p className="text-zinc-400 mt-4 max-w-xl leading-relaxed">
            Recovery Supps Australia supplies research chemicals for laboratory use only. Please read
            these policies carefully before ordering.
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-5 md:px-10 py-14 space-y-14">
        {SECTIONS.map((s, i) => (
          <Reveal key={s.id} delay={0.03}>
            <section id={s.id} className="scroll-mt-32" data-testid={`policy-${s.id}`}>
              <div className="flex items-center gap-3 mb-4">
                <span className="h-10 w-10 flex items-center justify-center border border-accent/40 bg-accent/5 text-accent">
                  <s.icon size={18} />
                </span>
                <h2 className="font-display font-black tracking-tight text-2xl sm:text-3xl leading-none">
                  {s.title}
                </h2>
              </div>
              <div className="space-y-3">
                {s.body.map((p, j) => (
                  <p key={j} className="text-zinc-400 leading-relaxed">{p}</p>
                ))}
              </div>
            </section>
          </Reveal>
        ))}

        <div className="border border-accent/40 bg-accent/5 p-5 clip-corner">
          <p className="font-mono-tech text-[10px] uppercase tracking-widest text-accent mb-2">
            Contact
          </p>
          <p className="text-zinc-300 text-sm leading-relaxed">
            Recovery Supps Australia · Melbourne, VIC · recoverysuppsaus@gmail.com — for COA
            requests, order issues or compliance questions.
          </p>
        </div>
      </div>
    </div>
  );
}
